// RestockModal — add stock to an ingredient via /ingredients/:id/adjust-stock.
// Role gate: MANAGER+ (enforced server-side). Consumed by IngredientListPage.

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState } from 'react';
import { Button, Input, TextArea, useToast } from '@bbpos/ui';
import { ingredientService } from '@/services/ingredient.service';
import type { Ingredient, StockAdjustmentType } from '@/services/ingredient.service';
import { ApiError } from '@/lib/api-client';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

interface Props {
  ingredient: Ingredient;
  onClose: () => void;
  onSuccess: () => void;
}

const TYPE_OPTIONS: { value: StockAdjustmentType; label: string; hint: string }[] = [
  { value: 'PURCHASE', label: 'Purchase', hint: 'Bought from vendor (needs unit cost)' },
  { value: 'ADJUSTMENT', label: 'Adjustment', hint: 'Manual correction (+/−)' },
  { value: 'DAMAGE', label: 'Damage', hint: 'Spoilage / wastage (reduces stock)' },
  { value: 'OPENING_STOCK', label: 'Opening', hint: 'Initial / opening balance' },
];

// Types that reduce stock — quantity is entered as a negative number.
const STOCK_OUT_TYPES: StockAdjustmentType[] = ['DAMAGE', 'ADJUSTMENT'];

export function RestockModal({ ingredient, onClose, onSuccess }: Props) {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);

  const [type, setType] = useState<StockAdjustmentType>('PURCHASE');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const qtyNum = Number(quantity);
  const newQty = Number(ingredient.stock_quantity) + (qtyNum || 0);

  const allowsStockOut = STOCK_OUT_TYPES.includes(type);

  const handleSubmit = async () => {
    if (!qtyNum || qtyNum === 0) {
      toast({ variant: 'warning', title: 'Enter a non-zero quantity' });
      return;
    }
    if (!allowsStockOut && qtyNum < 1) {
      toast({ variant: 'warning', title: 'Quantity must be at least 1 for this type' });
      return;
    }
    if (allowsStockOut && newQty < 0) {
      toast({ variant: 'warning', title: 'Adjustment would drop stock below zero' });
      return;
    }
    let unit_cost: number | undefined;
    if (type === 'PURCHASE') {
      const cost = Math.floor(Number(unitCost) * getSubunitMultiplier(currency)); // major → minor
      if (!cost || cost < 1) {
        toast({ variant: 'warning', title: 'Unit cost is required for a purchase' });
        return;
      }
      unit_cost = cost;
    }

    setSubmitting(true);
    try {
      await ingredientService.adjustStock(ingredient.id, {
        type,
        quantity: qtyNum,
        unit_cost,
        notes: notes.trim() || undefined,
      });
      toast({
        variant: 'success',
        title: 'Stock updated',
        description: `${ingredient.name}: +${qtyNum} ${ingredient.unit} (now ${newQty} ${ingredient.unit})`,
      });
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to update stock';
      toast({ variant: 'error', title: 'Restock failed', description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/60 backdrop-blur-sm animate-fade-in p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-100 dark:border-neutral-700 overflow-hidden animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-teal-500 via-emerald-500 to-success-500 p-5 text-white">
          <div className="text-xs uppercase tracking-wider text-emerald-100 mb-1">MANAGER+ action</div>
          <h2 className="text-lg font-semibold">Restock ingredient</h2>
          <p className="text-xs text-emerald-100 mt-1">{ingredient.name}</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <InfoCell label="Current stock" value={`${Number(ingredient.stock_quantity)} ${ingredient.unit}`} />
            <InfoCell
              label="After restock"
              value={`${qtyNum ? newQty : Number(ingredient.stock_quantity)} ${ingredient.unit}`}
              tone="success"
            />
          </div>

          {/* Adjustment type */}
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1.5">
              Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setType(opt.value)}
                  className={
                    'px-2 py-2 rounded-lg border text-sm transition-all ' +
                    (type === opt.value
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 shadow-sm'
                      : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800')
                  }
                >
                  <div className="font-medium">{opt.label}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1.5">
              {TYPE_OPTIONS.find((o) => o.value === type)?.hint}
            </p>
          </div>

          <Input
            label={allowsStockOut ? `Quantity to adjust (${ingredient.unit}, use − to reduce)` : `Quantity to add (${ingredient.unit})`}
            type="number"
            min={allowsStockOut ? undefined : 1}
            step={0.001}
            value={quantity}
            onChange={(v: string) => setQuantity(v)}
            placeholder={allowsStockOut ? 'e.g. -5' : 'e.g. 5'}
          />

          {type === 'PURCHASE' && (
            <Input
              label={`Unit cost (${symbol} per ${ingredient.unit})`}
              type="number"
              min={0.01}
              step={0.01}
              value={unitCost}
              onChange={(v: string) => setUnitCost(v)}
              helperText="Used to recompute weighted-average cost (WAC)"
            />
          )}

          <TextArea
            label="Notes (optional)"
            value={notes}
            onChange={(v: string) => setNotes(v)}
            placeholder="e.g. Purchased from vendor Y"
            maxLength={500}
            rows={2}
          />

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} loading={submitting}>Add stock</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCell({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'success';
}) {
  return (
    <div
      className={
        'rounded-lg border p-3 ' +
        (tone === 'success'
          ? 'bg-success-50 border-success-200 dark:bg-success-900/20 dark:border-success-800'
          : 'bg-neutral-50 border-neutral-200 dark:bg-neutral-800/60 dark:border-neutral-700')
      }
    >
      <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{label}</div>
      <div className="text-lg font-bold tabular-nums text-neutral-900 dark:text-white">{value}</div>
    </div>
  );
}
